import React, {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import Modal from 'react-bootstrap/Modal';
import {HibuTextButton} from '../hibuTextButton/HibuTextButton';
import './hibuModal.scss';
import Markdown from 'markdown-to-jsx';

const DEFAULTS = {
    backdrop: 'static' as const,
    keyboard: false,
    size: 'sm' as const,
    centered: true,
    closeButton: true,
    useMarkdown: false,
};

type ModalOptions = {
    backdrop?: boolean | 'static';
    keyboard?: boolean;
    size?: 'sm' | 'lg' | 'xl';
    centered?: boolean;
    closeButton?: boolean;
    title?: string;
    cancel?: string;
    ok?: string;
    dialogClassName?: string;
    useMarkdown?: boolean;
};

// custom context
type Ctx = { confirm: (message: string, options?: ModalOptions) => Promise<boolean> };
const ModalContext = createContext<Ctx | null>(null);

type Job = { message: string; options?: ModalOptions; resolve: (v: boolean) => void };

export function ModalProvider({children}: Readonly<{ children: React.ReactNode }>) {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<ModalOptions>({});
    const [message, setMessage] = useState('');
    const [queue, setQueue] = useState<Job[]>([]);
    const [current, setCurrent] = useState<Job | null>(null);

    const resolver = useRef<((v: boolean) => void) | null>(null);

    // clean up queue on unmount
    const queueRef = useRef<Job[]>([]);

    useEffect(() => {
        queueRef.current = queue;
    }, [queue]);

    // confirm
    const confirm = useCallback<Ctx['confirm']>((message, options) => {
        return new Promise<boolean>((resolve) => {
            setQueue((q) => [...q, {message, options, resolve}]);
        });
    }, []);

    // pump the queue whenever nothing is showing and there is pending work
    useEffect(() => {
        if (open || current || queue.length === 0) return;
        const next = queue[0];
        setCurrent(next);
        setMessage(next.message);
        setOptions(next.options ?? {});
        resolver.current = next.resolve;
        setOpen(true);
        setQueue((q) => q.slice(1)); // remove job that just started
    }, [open, current, queue]);

    // close modal
    const close = (v: boolean) => {
        setOpen(false);
        resolver.current?.(v);
        resolver.current = null;
        setCurrent(null); // next job (if any) will be picked up by the effect above
    };

    // clean up on unmount (resolve current and queued jobs with false)
    useEffect(() => {
        return () => {
            if (resolver.current) {
                resolver.current(false); // safely resolve Promise
                resolver.current = null;
            }
            if (queueRef.current.length) {
                queueRef.current.forEach((job) => {
                    try {
                        job.resolve(false);
                    } catch {
                    }
                });
            }
        };
    }, []);

    // memoize context value (stable reference)
    const value = useMemo<Ctx>(() => ({confirm}), [confirm]);

    return (
        <ModalContext.Provider value={value}>
            {children}
            <Modal
                show={open}
                onHide={() => close(false)}
                dialogClassName={options?.dialogClassName + ' hibu-modal'}
                backdropClassName="hibu-modal-backdrop"
                backdrop={options.backdrop ?? DEFAULTS.backdrop}
                keyboard={options.keyboard ?? DEFAULTS.keyboard}
                size={options.size ?? DEFAULTS.size}
                centered={options.centered ?? DEFAULTS.centered}
                aria-labelledby="hibu-modal-title"
            >
                {createModalHeader({title: options.title, closeButton: options.closeButton})}
                {createModalBody({
                    children: (options.useMarkdown ?? DEFAULTS.useMarkdown) ?
                        <Markdown className="mb-0">{message}</Markdown> : message
                })}
                {createModalFooter({
                        onCancel: {
                            label: options.cancel, action: () => {
                                close(false);
                            }
                        },
                        onOk: {
                            label: options.ok, action: () => {
                                close(true);
                            }
                        }
                    }
                )}
            </Modal>
        </ModalContext.Provider>
    );
}

// header
type HeaderOptions = {
    title?: string;
    closeButton?: boolean;
};

function createModalHeader({title, closeButton}: HeaderOptions) {
    return (
        <Modal.Header closeButton={closeButton ?? DEFAULTS.closeButton}>
            <Modal.Title id="hibu-modal-title">{title ?? ''}</Modal.Title>
        </Modal.Header>
    );
}

// body
type BodyOptions = {
    children?: ReactNode;
}

function createModalBody({children}: BodyOptions) {
    return (
        <Modal.Body>
            {children}
        </Modal.Body>
    );
}

// footer
type FooterOptions = {
    onCancel: {
        label?: string;
        action: () => void
    }
    onOk: {
        label?: string;
        action: () => void;
        form?: string;
        type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
        disabled?: boolean
    }
}

function createModalFooter({onCancel, onOk}: FooterOptions) {
    return (
        <Modal.Footer>
            {onCancel.label && <HibuTextButton label={onCancel.label} onClick={onCancel.action} filled={false}
                                               disabled={false}/>}
            {onOk.label && <HibuTextButton label={onOk.label} onClick={onOk.action} filled={true}
                                           disabled={onOk.disabled ?? false} type={onOk.type} form={onOk.form}/>}
        </Modal.Footer>
    );
}

// custom hook to access the confirm function from ModalProvider
export function useConfirm() {
    const ctx = useContext(ModalContext);
    if (!ctx) throw new Error('useConfirm must be used within ModalProvider');
    return ctx.confirm;
}

// custom hook which returns an onClick handler
export function useWithConfirmation(message: string, options?: ModalOptions) {
    const confirm = useConfirm();
    return {
        onClick:
            <E = Element>(action?: React.MouseEventHandler<E> | (() => void)): React.MouseEventHandler<E> =>
                async (e) => {
                    e.stopPropagation?.();
                    e.preventDefault?.();

                    const ok = await confirm(message, options);
                    if (!ok || !action) return;

                    // if action expects argument, treat it as event handler
                    if ((action as Function).length > 0) {
                        (action as React.MouseEventHandler<E>)(e);
                    } else {
                        (action as () => void)();
                    }
                },
    };
}

// special case: save search modal
type SaveSearchModalOptions = {
    show: boolean;
    onHide: () => void;
    title: string;
    children: ReactNode;
    cancel: string;
    save: string;
    formId: string,
    canBeSaved: boolean;
};

export function SaveSearchModal({
                                    show,
                                    onHide,
                                    title,
                                    children,
                                    cancel,
                                    save,
                                    formId,
                                    canBeSaved
                                }: SaveSearchModalOptions) {
    return (
        <Modal id={'save-search-modal'}
               show={show}
               onHide={onHide}
               dialogClassName="hibu-modal"
               backdropClassName="hibu-modal-backdrop"
               backdrop={true}
               keyboard={true}
               centered={true}
        >
            {createModalHeader({title: title, closeButton: false})}
            {createModalBody({children: children})}
            {createModalFooter({
                    onCancel: {
                        label: cancel, action: onHide
                    },
                    onOk: {
                        label: save, action: onHide, form: formId, type: 'submit', disabled: !canBeSaved
                    }
                }
            )}
        </Modal>
    );
}
